import React from "react";
import PropTypes from "prop-types";
import {
  money,
  invoiceText,
  pagesText,
  orderText,
  fileOf,
  dateText,
  customerPrintedOf,
  suggestedRefundOf,
  accountLabel,
  accountText,
} from "./printErrorLabels";
import CopyButton from "./CopyButton";

// A group of fields beside its name; under it on a phone
const section = (title, body) => (
  <div className="sm:flex">
    <h3 className="flex-shrink-0 mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase w-28 sm:mb-0 dark:text-gray-400">
      {title}
    </h3>
    <div className="flex flex-wrap flex-1 min-w-0 gap-x-8 gap-y-2">{body}</div>
  </div>
);

// wide: a text of any length, on a line of its own
const field = (label, value, wide) => (
  <div className={"min-w-0" + (wide ? " w-full" : "")}>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p
      className={
        "text-gray-700 break-words dark:text-gray-300" +
        (wide ? " overflow-y-auto max-h-36" : "")
      }
    >
      {value}
    </p>
  </div>
);

// A bundle keeps half pages, which the backend calls units
const bundlePages = (units) =>
  units === undefined || units === null ? "—" : String(units / 2).replace(".5", ",5") + " хуудас";

const phoneLink = (phone) => (
  <a
    href={"tel:" + phone}
    className="font-semibold text-purple-600 dark:text-purple-400 hover:underline"
  >
    {phone}
  </a>
);

// What the list leaves out of a print error's row. Read-only: a pending error is decided
// in ResolvePrintError.
const PrintErrorDetail = ({ printError }) => {
  const { status } = printError;
  // no_payment has no invoice or refund and is never decided
  const noPayment = status === "no_payment";
  const decided = status && status !== "pending" && !noPayment;
  // Left by the customer through the kiosk's QR
  const contact = printError.contact || {};
  // Their account, if they were logged in while uploading
  const user = printError.user;
  const customerPrinted = customerPrintedOf(printError);
  const suggestion = suggestedRefundOf(printError);
  const order = orderText(printError);

  return (
    // No wider than a phone's screen, where the table itself scrolls sideways
    <div className="space-y-4 text-sm" style={{ maxWidth: "calc(100vw - 5rem)" }}>
      {section(
        "Хэвлэлт",
        <>
          {field("Хэвлэгдсэн / нийт", pagesText(printError))}
          {order && field("Захиалга", order)}
          {field("Файл", fileOf(printError))}
          {field("Event ID", printError.event_id || "—")}
          {printError.error_message &&
            field("Киоскийн мэдэгдсэн алдаа", printError.error_message, true)}
        </>
      )}

      {section(
        "Төлбөр",
        <>
          {!noPayment &&
            field(
              "Нэхэмжлэх",
              <span className="font-mono text-xs">{invoiceText(printError)}</span>
            )}
          {field("Төлбөрийн төрөл", printError.payment_type || "—")}
          {printError.payment_type === "bundle" ? (
            <>
              {/* Its pages go back to the bundle, half pages kept as units */}
              {field("Төлбөр", "Багцаас")}
              {printError.refund_photos > 0 ? (
                field(
                  status === "refunded" ? "Буцаасан зураг" : "Санал болгож буй буцаалт",
                  printError.refund_photos + " зураг (багийн зураг)"
                )
              ) : (
                <>
                  {status === "pending" &&
                    field("Санал болгож буй буцаалт", bundlePages(printError.refund_units))}
                  {status === "refunded" &&
                    field("Буцаасан хуудас", bundlePages(printError.refund_units))}
                </>
              )}
            </>
          ) : (
            <>
              {field(noPayment ? "Дүн" : "Төлсөн дүн", money(printError.amount))}
              {status === "pending" &&
                field(
                  "Санал болгож буй буцаалт",
                  <>
                    {money(suggestion.amount)}
                    {suggestion.customerPrinted !== null && (
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        Хэрэглэгчийн тоогоор ({suggestion.customerPrinted}/{printError.pages}{" "}
                        зөв), киоск тоолоогүй
                      </span>
                    )}
                  </>
                )}
              {status === "refunded" &&
                field("Буцаасан дүн", money(printError.refund_amount))}
            </>
          )}
          {!noPayment && !printError.paymentLogId && (
            <p className="w-full text-xs font-semibold text-yellow-700 dark:text-yellow-400">
              Төлбөр олдсонгүй: энэ invoice-оор төлсөн төлбөрийн бичлэг системд алга.
            </p>
          )}
        </>
      )}

      {(contact.phone || !user) &&
        section(
          "Хэрэглэгч",
          contact.phone ? (
            <>
              {field("Утас (QR-аар)", phoneLink(contact.phone))}
              {customerPrinted !== null &&
                field(
                  "Зөв хэвлэгдсэн (хэрэглэгчийн хэлснээр)",
                  customerPrinted + " / " + (printError.pages ?? "—")
                )}
              {contact.account_number && (
                <>
                  {/* The IBAN names the bank; only older contacts carry one. */}
                  {contact.bank && field("Банк", contact.bank)}
                  {field(
                    accountLabel(contact.account_number),
                    <>
                      {accountText(contact.account_number)}
                      <CopyButton value={contact.account_number} />
                    </>
                  )}
                  {field("Данс эзэмшигч", contact.account_name)}
                </>
              )}
              {field("Илгээсэн", dateText(contact.date))}
            </>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">
              Холбоо барих мэдээлэл үлдээгээгүй.
            </p>
          )
        )}

      {user &&
        section(
          "Бүртгэл",
          <>
            {field("Нэр", user.name)}
            {field("Утас", user.phone ? phoneLink(user.phone) : "—")}
            {field("Имэйл", user.email)}
          </>
        )}

      {decided &&
        section(
          "Шийдвэр",
          <>
            {field("Шийдсэн", printError.resolvedByName || "—")}
            {field("Огноо", dateText(printError.resolved_at))}
            {printError.note && field("Тайлбар", printError.note, true)}
          </>
        )}
    </div>
  );
};

PrintErrorDetail.propTypes = {
  printError: PropTypes.object.isRequired,
};

export default PrintErrorDetail;
